import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as campaignDocumentActions from '@redux/modules/campaignDocument'

import defaultFormState, { requiredFields } from '@models/campaignDocument'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { CampaignDocumentModel, CampaignDocumentRequestOptions } from '@models/types'

type CreateCampaignDocumentParams = {
  campaignDocumentParams: Partial<CampaignDocumentModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignDocumentRequestOptions,
}

const createCampaignDocument = (params: CreateCampaignDocumentParams) => {
  const { dispatch, campaignDocumentParams, requestOptions } = params
  const { createCampaignDocument: createFn } = campaignDocumentActions

  return dispatch(createFn(campaignDocumentParams, requestOptions))
}

type UpdateCampaignDocumentParams = {
  campaignDocument: CampaignDocumentModel,
  campaignDocumentParams: Partial<CampaignDocumentModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignDocumentRequestOptions,
}

const updateCampaignDocument = (params: UpdateCampaignDocumentParams) => {
  const {
    dispatch, campaignDocument, campaignDocumentParams, requestOptions,
  } = params
  const { updateCampaignDocument: updateFn } = campaignDocumentActions

  const updatedParams = {
    id: campaignDocument.id,
    ...campaignDocumentParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCampaignDocumentParams = {
  campaignDocument: DeleteParams<CampaignDocumentModel>,
  dispatch: AppDispatch,
}

const deleteCampaignDocument = (params: DeleteCampaignDocumentParams) => {
  const { dispatch, campaignDocument } = params
  const { deleteCampaignDocument: deleteFn } = campaignDocumentActions

  return dispatch(deleteFn(campaignDocument))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignDocumentForm(
  campaignDocument: Partial<CampaignDocumentModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignDocumentForm = useForm(
    defaultFormState,
    { entity: campaignDocument, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignDocument.id, campaignDocument.cache_key],
  )

  return {
    ...campaignDocumentForm,
  }
}

export const useRelations = (campaignDocument: Partial<CampaignDocumentModel> = {}) => {
  const { campaign_id } = campaignDocument

  const { campaigns } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}

  return {
    campaign,
  }
}

function useCampaignDocument(initEntity: Partial<CampaignDocumentModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: campaignDocument }:
    // @ts-expect-error TS2345
    { entity: CampaignDocumentModel } = useLatestEntity(initEntity, 'campaignDocuments')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaign } = useRelations(campaignDocument)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignDocuments)

  return {
    callbacks: {
      createCampaignDocument: (
        campaignDocumentParams: Partial<CampaignDocumentModel>,
        entityOptions?: CampaignDocumentRequestOptions,
      ) => (
        createCampaignDocument({ campaignDocumentParams, dispatch, requestOptions: entityOptions })
      ),
      addCampaignDocument: () => launchModal({
        callbacks,
        modalKey: 'CreateCampaignDocumentModal',
        payload: { campaignDocument },
      }),
      deleteCampaignDocument: () => deleteCampaignDocument({ dispatch, campaignDocument }),
      updateCampaignDocument: (
        campaignDocumentParams: Partial<CampaignDocumentModel>,
        entityOptions?: CampaignDocumentRequestOptions,
      ) => (
        updateCampaignDocument({
          campaignDocument, campaignDocumentParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    campaignDocument,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignDocument.id),
    updating,
  }
}

export default useCampaignDocument
